import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import s from './teachers.module.scss'
import { FormProvider, useForm } from 'react-hook-form';
import { Loader as Spinner } from '@shared/ui/loader/loader';
import { useMedia } from 'use-media';
import { PageTemplate } from '@shared/ui/page-template';
import { SearchTeacher } from '@widgets/student/ui/search-teacher/search-teacher';
import { TeacherCard } from '@widgets/student/ui/teacher-card/teacher-card';
import { LoaderDesktop } from '@widgets/student/ui/teacher-card/loader';
import { useAllTeachers } from '@entities/student/api/useAllTeachers';
import ReactPaginate from 'react-paginate';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useAvailableInstruments } from '@shared/api/useAvailableInstruments';

const DAY_FORM_BY_API = { weekend: 'weekends', weekday: 'weekdays' };
const DAY_API_BY_FORM = { weekends: 'weekend', weekdays: 'weekday' };

// Первичные значения фильтров из query-параметров (переход со страницы подбора преподавателей)
const getInitialFilterValues = (searchParams) => {
	const instruments = searchParams.getAll('instruments').map(Number).filter(Boolean);
	const daysType = DAY_FORM_BY_API[searchParams.getAll('days_type')[0]] || '';
	const timeOfDay = searchParams.getAll('time_of_day')[0] || '';
	return { instruments, daysType, timeOfDay };
};

export const Teachers = () => {
	const [searchParams] = useSearchParams();
	const initialFilterValues = useMemo(() => getInitialFilterValues(searchParams), [searchParams]);
	const [page, setPage] = useState(1)
	const [filters, setFilters] = useState(() => ({
		...(initialFilterValues.instruments.length ? { instruments: initialFilterValues.instruments } : {}),
		...(initialFilterValues.daysType ? { days_type: [DAY_API_BY_FORM[initialFilterValues.daysType]] } : {}),
		...(initialFilterValues.timeOfDay ? { time_of_day: [initialFilterValues.timeOfDay] } : {}),
	}))

	// Приоритетный порядок инструментов в фильтре: остальные идут после них в исходном порядке
	const INSTRUMENTS_PRIORITY = ['Гитара', 'Вокал', 'Фортепиано', 'Флейта', 'Скрипка']
	const normalizeName = (name) => (name || '').replace(/[^А-Яа-яA-Za-z]/g, '')
	const sortInstrumentsByPriority = (groups) => {
		const sorted = [...(groups || []).flatMap((group) => group?.instruments || [])]
			.sort((a, b) => {
				const indexA = INSTRUMENTS_PRIORITY.indexOf(normalizeName(a?.name))
				const indexB = INSTRUMENTS_PRIORITY.indexOf(normalizeName(b?.name))
				return (indexA === -1 ? INSTRUMENTS_PRIORITY.length : indexA) -
					(indexB === -1 ? INSTRUMENTS_PRIORITY.length : indexB)
			})
		return sorted.length ? [{ instruments: sorted }] : groups
	}

	useEffect(() => {
		localStorage.setItem('student_has_viewed_teachers', 'true')
	}, [])

	const{data: teachers, isFetched} = useAllTeachers({...filters, page})
	const{data: instruments} = useAvailableInstruments()
	const sortedInstruments = sortInstrumentsByPriority(instruments)
	
	const methods = useForm({
		defaultValues: {
			instruments: initialFilterValues.instruments,
			free_first_lesson: false,
			works_with_children: false,
			verified_docs: false,
			timeOfDay: initialFilterValues.timeOfDay,
			daysType: initialFilterValues.daysType,
			sort_by: 'recommended',
		}
	})
	const {control, handleSubmit} = methods

	const isMobile = useMedia({maxWidth: '768px'})

  const onSubmit = handleSubmit(data => {
		const payload = {
			q: data?.q,
			instruments: data?.instruments,
			...(data?.free_first_lesson ? { free_first_lesson: 1 } : {}),
			...(data?.works_with_children ? { works_with_children: 1 } : {}),
			...(data?.verified_docs ? { verified_docs: 1 } : {}),
			...(data?.timeOfDay ? { time_of_day: [data.timeOfDay] } : {}),
			...(data?.daysType ? { days_type: [DAY_API_BY_FORM[data.daysType] || data.daysType] } : {}),
			...(data?.sort_by && data.sort_by !== 'recommended' ? { sort: data.sort_by } : {}),
		}

		setFilters({...payload})
  })

	return (
		<PageTemplate title='Поиск наставников' contentClassName={s.content}>
			<FormProvider {...methods}>
				<SearchTeacher {...{onSubmit, control, instruments: sortedInstruments}}/>
			</FormProvider>
			{!isFetched ? 
					<div className={s.groupTeachers}>
						<p className={s.groupTitle}>
							Все преподаватели
						</p>
						<div className={s.flex}>
							{!isMobile ? 
								<LoaderDesktop/> :
								<Spinner/>}
						</div>
					</div> : 
					<>
						{teachers?.my?.length > 0 || teachers?.all?.data.length > 0 ? 
							<div className={s.content}>
								<div className={s.mainColumn}>
									{teachers?.my?.length > 0 && <div className={s.groupTeachers}>
										<p className={s.groupTitle}>
											Мои преподаватели
										</p>
										<div className={s.flex}>
											{teachers?.my?.map((teacher) => (
												<TeacherCard key={teacher.id} {...teacher}/>
											))}
										</div>
									</div>}
									{teachers?.all?.data.length > 0 && <div className={s.groupTeachers}>
										<p className={s.groupTitle}>
											Все преподаватели
										</p>
										<div className={s.flex}>
											{teachers?.all?.data?.map((teacher) => (
												<TeacherCard key={teacher.id} {...teacher}/>
											))}
										</div>
									</div>}
									<ReactPaginate
										breakLabel="..."
										forcePage={page - 1}
										nextLabel={<IconChevronRight size={20}/>}
										onPageChange={(e) => setPage(e.selected + 1)}
										pageRangeDisplayed={5}
										pageCount={teachers?.all?.last_page}
										previousLabel={<IconChevronLeft size={20}/>}
										className={s.paginator}
										previousClassName={s.prev}
										nextClassName={s.next}
										disabledClassName={s.disabled}
										pageLinkClassName={s.item}
										activeLinkClassName={s.active}
									/>
								</div>
							</div> : 
							<div className={s.emptyText}>
								Мы не нашли подходящих преподавателей
							</div>
						}
					</>
				}
		</PageTemplate>
	)
}
